import { Box, Button, Container, Fieldset, Flex, Image, Input, Text } from '@chakra-ui/react';
import logo from '@/assets/icon/logo.svg';
import { useForm } from 'react-hook-form';
import { fetchAPI } from '@/api/fetch';
import { apiForgetPassword } from '@/api/services';
import InfoCard from '@/components/data-display/InfoCard';
import { InfoCardType } from '@/components/data-display/InfoCardTypes';
import { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { CaretLeft } from '@phosphor-icons/react';

type FormValues = {
  email: string;
};

export default function ForgetPassword() {
  const [isIncorrect, setIsIncorrect] = useState(false);
  const [resetEmailSent, setResetEmailSent] = useState(false);
  const [userEmail, setUserEmail] = useState('');
  const navigate = useNavigate();

  const {
    handleSubmit,
    register,
    formState: { errors, isSubmitting }
  } = useForm<FormValues>({ shouldFocusError: false, mode: 'onChange' });

  async function onSubmit(values: FormValues) {
    setIsIncorrect(false);
    setResetEmailSent(false);

    const response = await fetchAPI<void>({
      request: () =>
        apiForgetPassword({
          email: values.email
        })
    });

    if (!response.success) {
      if (response.code?.toString().startsWith('4')) {
        setIsIncorrect(true);
      }
    } else {
      setResetEmailSent(true);
      setUserEmail(values.email);
    }
  }

  return (
    <Container
      maxW="100%"
      minH="100dvh"
      w="100%"
      h="100%"
      p={0}
      bg="background.primary"
      display="flex"
      flexDir="column"
    >
      <header style={{ position: 'fixed', top: 0, width: '100%', zIndex: 1000 }}>
        <Flex p="32px 50px">
          <Image
            src={logo}
            alt="logo"
            width="145px"
            onClick={() => navigate('/')}
            cursor="pointer"
          />
        </Flex>
      </header>
      <Flex flexGrow={1} mx="auto" overflowY="auto" pb="60px">
        <form onSubmit={handleSubmit(onSubmit)}>
          <Fieldset.Root
            borderRadius="10px"
            w="450px"
            h="725px"
            display="flex"
            flexDirection="column"
            justifyContent="center"
          >
            <Box>
              <Flex justifyContent="left" pb="32px" alignItems="center" gap="16px">
                <CaretLeft
                  weight="bold"
                  size={32}
                  color="neutrals.900"
                  onClick={() => navigate(-1)}
                  cursor="pointer"
                />
                <Text fontSize="36px" fontWeight="bold">
                  {resetEmailSent ? 'Check your email' : 'Forgot password'}
                </Text>
              </Flex>
              <Flex justifyContent="left">
                <Text fontSize="16px" color="neutrals.700">
                  {resetEmailSent ? (
                    <>
                      Please check the email address <strong>{userEmail}</strong> for instructions
                      to reset your password.
                    </>
                  ) : (
                    "Enter the email address you registered with and we'll send you instructions to reset your password."
                  )}
                </Text>
              </Flex>
              {isIncorrect && (
                <InfoCard
                  my="16px"
                  type={InfoCardType.Warning}
                  title={`The email hasn't been registered.`}
                  contents={
                    <Flex fontSize="14px" mt="5px" display="inline">
                      The email address entered does not exist. Please provide a valid email address
                      (e.g., name@example.com).
                    </Flex>
                  }
                ></InfoCard>
              )}
              <Box mt="20px">
                <Text
                  fontWeight={500}
                  fontSize="16px"
                  color="neutrals.900"
                  mb="10px"
                  hidden={resetEmailSent}
                >
                  Email
                </Text>
                <Input
                  fontSize="16px"
                  height="48px"
                  borderRadius="8px"
                  id="login-email"
                  placeholder="name@email.com"
                  _placeholder={{
                    color: 'neutrals.300'
                  }}
                  _focus={{
                    boxShadow: 'none',
                    outline: 'none'
                  }}
                  disabled={resetEmailSent}
                  hidden={resetEmailSent}
                  borderColor={errors.email ? 'error.700' : 'neutrals.300'}
                  variant="outline"
                  type="email"
                  p="12px 16px"
                  h="50px"
                  autoComplete="email"
                  _invalid={{
                    borderWidth: 1,
                    borderColor: 'error.700'
                  }}
                  {...register('email', {
                    required: 'Email is required.',
                    pattern: {
                      value: /^[a-zA-Z0-9._%+-]+@[a-zA-Z0-9.-]+\.[a-zA-Z]{2,}$/,
                      message: 'Please enter a valid email address.'
                    }
                  })}
                  onChange={() => {
                    setIsIncorrect(false);
                    setResetEmailSent(false);
                  }}
                />
                {errors.email && (
                  <Text fontSize="16px" color="error.700" mt="5px" ml="5px">
                    {errors.email.message}
                  </Text>
                )}
              </Box>
            </Box>
            <Box mt="20px" display="flex" justifyContent="center">
              <Button
                loading={isSubmitting}
                bg="neutrals.900"
                w="100%"
                h="60px"
                color="neutrals.0"
                borderRadius="8px"
                type="submit"
                cursor={isSubmitting ? 'not-allowed' : 'pointer'}
                fontSize="16px"
                fontWeight="semibold"
                lineHeight="20px"
                display="flex"
                alignItems="center"
                justifyContent="center"
              >
                {resetEmailSent ? 'Resend email' : 'Send instructions'}
              </Button>
            </Box>
          </Fieldset.Root>
        </form>
      </Flex>
    </Container>
  );
}
